import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { movieCategories } from "@/mocks/categories";

const navItems = [
  { label: "首页", path: "/" },
  { label: "电影分类", path: "/category" },
  { label: "影评专题", path: "/special" },
  { label: "关于我们", path: "/about" },
  { label: "联系我们", path: "/contact" },
];

export default function SiteNavbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSearch, setMobileSearch] = useState("");

  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.search]);

  const solid = scrolled || !isHome;

  const textColor = solid ? "text-foreground-900" : "text-background-50";

  const submitMobileSearch = () => {
    const q = mobileSearch.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setMobileSearch("");
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          solid
            ? "bg-background-50/95 backdrop-blur border-b border-background-200"
            : "bg-gradient-to-b from-foreground-950/50 to-transparent"
        }`}
      >
        <div className="w-full px-4 md:px-10 h-16 md:h-20 flex items-center justify-between gap-4">
          {/* brand */}
          <Link to="/" className="flex items-center gap-3 shrink-0 cursor-pointer">
            <span
              className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                solid ? "bg-primary-500 text-background-50" : "bg-background-50/90 text-primary-600"
              }`}
            >
              <i className="ri-film-line text-xl"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className={`font-heading font-bold text-lg md:text-xl tracking-wide ${textColor}`}>
                爱的色放电影
              </span>
              <span
                className={`text-[10px] md:text-xs tracking-[0.2em] mt-0.5 ${
                  solid ? "text-foreground-500" : "text-background-200"
                }`}
              >
                LOVE ON FILM
              </span>
            </span>
          </Link>

          {/* desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              const active =
                item.path === "/"
                  ? location.pathname === "/"
                  : location.pathname.startsWith(item.path);
              return item.path === "/category" ? (
                <div key={item.path} className="relative group">
                  <Link
                    to={item.path}
                    className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-1 cursor-pointer transition-colors ${
                      active
                        ? solid
                          ? "text-primary-600"
                          : "text-background-50"
                        : solid
                        ? "text-foreground-700 hover:text-primary-600"
                        : "text-background-100 hover:text-background-50"
                    }`}
                  >
                    {item.label}
                    <i className="ri-arrow-down-s-line text-base"></i>
                  </Link>
                  <div className="absolute left-0 top-full pt-2 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
                    <div className="w-64 bg-background-50 border border-background-200 rounded-lg p-2 grid grid-cols-2 gap-1">
                      {movieCategories.map((c) => (
                        <Link
                          key={c.id}
                          to={`/category?genre=${encodeURIComponent(c.genre)}`}
                          className="px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 cursor-pointer flex items-center gap-2 whitespace-nowrap"
                        >
                          <i className={`${c.icon} text-base`}></i>
                          {c.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors ${
                    active
                      ? solid
                        ? "text-primary-600"
                        : "text-background-50"
                      : solid
                      ? "text-foreground-700 hover:text-primary-600"
                      : "text-background-100 hover:text-background-50"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/search"
              className={`hidden md:flex items-center gap-2 px-4 py-2 rounded-md text-sm cursor-pointer transition-colors ${
                solid
                  ? "bg-background-100 text-foreground-600 hover:bg-background-200"
                  : "bg-background-50/20 text-background-50 hover:bg-background-50/30"
              }`}
            >
              <i className="ri-search-line text-base"></i>
              搜索电影
            </Link>
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="打开菜单"
              className={`lg:hidden w-10 h-10 rounded-md flex items-center justify-center cursor-pointer ${
                solid ? "text-foreground-900 hover:bg-background-200" : "text-background-50 hover:bg-background-50/20"
              }`}
            >
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-background-50 border-b border-background-200 max-h-[70vh] overflow-y-auto">
            <nav className="px-4 py-3 flex flex-col">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="px-3 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600 cursor-pointer border-b border-background-100"
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/search"
                className="px-3 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600 cursor-pointer"
              >
                搜索电影
              </Link>
            </nav>
          </div>
        )}
      </header>

      {/* mobile floating search */}
      <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-background-50 border-t border-background-200 px-4 py-3">
        <div className="flex items-center gap-2 bg-background-100 rounded-full px-4 py-2">
          <i className="ri-search-line text-lg text-foreground-500"></i>
          <input
            value={mobileSearch}
            onChange={(e) => setMobileSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submitMobileSearch()}
            placeholder="搜索爱情电影、影评…"
            className="flex-1 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 outline-none"
          />
          <button
            type="button"
            onClick={submitMobileSearch}
            className="text-sm font-medium text-primary-600 px-2 py-1 cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </div>
      </div>
    </>
  );
}